import React, { useMemo, useState, useEffect } from 'react';
import Card from 'components/card';
import { FaRegClock, FaUsers, FaChevronDown, FaMapMarkerAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';
import useCalendarData from 'hooks/useCalendarData';
import { fireSwal } from 'utils/swalConfig';

// --- Sub-Components (Updated to use extendedProps) ---

// Component for the MENTOR's view
const MentorEventDisplay = ({ event, isExpanded, onToggle }) => {
    return (
        <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out overflow-hidden">
            <div className="flex items-center p-4">
                {/* Date Block */}
                <div className="flex-shrink-0 mr-5">
                    <div className="flex flex-col items-center justify-center w-20 h-20 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-md">
                        <span className="text-xl font-bold -mb-1">{moment(event.start).format('DD')}</span>
                        <span className="text-sm font-semibold uppercase">{moment(event.start).format('MMM')}</span>
                        <span className="text-xs opacity-80">{moment(event.start).format('YYYY')}</span>
                    </div>
                </div>

                {/* Event Details */}
                <div className="flex-grow min-w-0">
                    <p className="text-lg font-bold text-navy-700 dark:text-white truncate" title={event.title}>{event.title}</p>
                    {/* FIXED: Accessing description from extendedProps */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{event.extendedProps.description}</p>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-300 mt-2">
                        <FaMapMarkerAlt className="mr-1.5" />
                        <span>{event.extendedProps.location}</span>
                    </div>
                </div>

                {/* Teams Toggle Button */}
                {event.extendedProps.teams.length > 0 && (
                    <button onClick={onToggle} className="ml-4 flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-navy-700 hover:bg-gray-200 dark:hover:bg-navy-600 transition-colors duration-200">
                        <FaUsers className="text-orange-500 text-lg" />
                        <span className="text-xs font-semibold text-navy-700 dark:text-white mt-1">{event.extendedProps.teams.length} {event.extendedProps.teams.length > 1 ? 'Teams' : 'Team'}</span>
                        <FaChevronDown className={`h-3 w-3 text-gray-500 dark:text-gray-400 mt-0.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                )}
            </div>

            {/* Expandable Team List */}
            <div className={`transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                <div className="px-6 pb-4 pt-2 border-t border-gray-100 dark:border-navy-700">
                    <h5 className="font-semibold text-sm text-navy-700 dark:text-white mb-2">Registered Teams:</h5>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
                        {event.extendedProps.teams.map((team) => <li key={team.id} className="text-sm truncate">{team.name}</li>)}
                    </ul>
                </div>
            </div>
        </div>
    );
};

// Component for the USER's view with reservation logic
const UserEventDisplay = ({ event, isReserving, onReserveClick, onCancel, onSubmit, details, onDetailsChange, isAlreadyReserved }) => {
    return (
        <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out overflow-hidden">
            <div className="flex items-center p-4">
                {/* Date Block */}
                <div className="flex-shrink-0 mr-5">
                    <div className="flex flex-col items-center justify-center w-20 h-20 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-md">
                        <span className="text-xl font-bold -mb-1">{moment(event.start).format('DD')}</span>
                        <span className="text-sm font-semibold uppercase">{moment(event.start).format('MMM')}</span>
                        <span className="text-xs opacity-80">{moment(event.start).format('YYYY')}</span>
                    </div>
                </div>

                {/* Event Details */}
                <div className="flex-grow min-w-0">
                    <p className="text-lg font-bold text-navy-700 dark:text-white truncate" title={event.title}>{event.title}</p>
                    {/* FIXED: Accessing description from extendedProps */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{event.extendedProps.description}</p>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-300 mt-2">
                        <FaMapMarkerAlt className="mr-1.5" />
                        <span>{event.extendedProps.location}</span>
                    </div>
                </div>

                {/* Reserve Button */}
                {!isReserving && (
                   <button 
                        onClick={onReserveClick} 
                        disabled={isAlreadyReserved}
                        className={`ml-4 flex-shrink-0 rounded-lg px-5 py-2 font-semibold text-white transition-colors ${
                            isAlreadyReserved 
                            ? 'bg-orange-500/40 cursor-not-allowed' 
                            : 'bg-orange-500 hover:bg-orange-600'
                        }`}
                    >
                        {isAlreadyReserved ? 'Reserved' : 'Reserve'}
                    </button>
                )}
            </div>

            {/* Expandable Reservation Form */}
            <div className={`transition-all duration-500 ease-in-out ${isReserving ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                <div className="px-6 pb-4 pt-2 border-t border-gray-100 dark:border-navy-700">
                    <h5 className="font-semibold text-sm text-navy-700 dark:text-white mb-2">Reservation Details:</h5>
                    <textarea
                        className="w-full rounded-md border border-gray-300 dark:border-navy-600 bg-white dark:bg-navy-700 p-2 text-sm text-navy-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        rows="2"
                        placeholder="Add details if needed (e.g., project name, topics to discuss)..."
                        value={details}
                        onChange={onDetailsChange}
                    ></textarea>
                    <div className="mt-3 flex items-center gap-2">
                        <button onClick={onSubmit} className="flex-1 rounded-md bg-orange-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-orange-600">
                            Confirm Reservation
                        </button>
                        <button onClick={onCancel} className="rounded-md bg-gray-200 dark:bg-navy-600 px-3 py-1.5 text-sm font-semibold text-navy-700 dark:text-white hover:bg-gray-300">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main UpcomingEventsCard Component ---
const UpcomingEventsCard = () => {
    // Get all state and functions from the centralized hook.
    const { 
        isLoading, 
        events, 
        user, 
        myReservationEventIds, 
        createReservation 
    } = useCalendarData();

    // Local state for UI interactions
    const [expandedEventId, setExpandedEventId] = useState(null);
    const [reservingEventId, setReservingEventId] = useState(null);
    const [reservationDetails, setReservationDetails] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const EVENTS_PER_PAGE = user?.role === 'mentor' ? 3 : 4;

    // Filter the grouped events from the hook to get only upcoming ones
    const upcomingEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return events
            .filter(evt => new Date(evt.start) >= today)
            .sort((a, b) => new Date(a.start) - new Date(b.start));
    }, [events]);
    
    // Pagination logic
    useEffect(() => {
        setCurrentPage(0);
    }, [upcomingEvents]);
    
    const totalPages = Math.ceil(upcomingEvents.length / EVENTS_PER_PAGE);
    const paginatedEvents = useMemo(() => {
        const startIndex = currentPage * EVENTS_PER_PAGE;
        return upcomingEvents.slice(startIndex, startIndex + EVENTS_PER_PAGE);
    }, [upcomingEvents, currentPage]);

    // UI handler functions
    const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
    const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 0));
    const handleToggleTeams = (eventId) => setExpandedEventId(prevId => (prevId === eventId ? null : eventId));
    const handleReserveClick = (eventId) => {
        setReservingEventId(eventId);
        setExpandedEventId(null);
    };
    const handleCancelReservation = () => {
        setReservingEventId(null);
        setReservationDetails('');
    };

    // API interaction for submitting a reservation
    const handleSubmitReservation = async (event) => {
        const timelineIdToReserve = event.extendedProps.reservableTimelineId;

        if (!timelineIdToReserve) {
            fireSwal({ icon: 'error', title: 'Not Reservable', text: 'A reservation slot is not available for your team for this event.' });
            return;
        }

        try {
            await createReservation({ 
                timelineId: timelineIdToReserve, 
                notes: reservationDetails 
            });
            fireSwal({
                icon: 'success',
                title: 'Reservation Confirmed!',
                text: `Your spot for "${event.title}" is now booked.`,
                timer: 2500,
                showConfirmButton: false
            });
            handleCancelReservation();
        } catch (error) {
            fireSwal({
                icon: 'error',
                title: 'Reservation Failed',
                text: error.response?.data?.message || 'An unexpected error occurred.',
            });
        }
    };

    return (
        <Card extra={"w-full h-full p-4 md:p-6 bg-gray-50 dark:bg-navy-900 rounded-3xl flex flex-col"}>
            <div className="flex justify-between items-center mb-4 px-2 flex-shrink-0">
                <div>
                    <h4 className="text-2xl font-bold text-navy-700 dark:text-white">Upcoming Events</h4>
                    <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {user?.role === 'mentor' ? "Here's a look at what's happening next." : "Reserve a spot in an upcoming session."}
                    </p>
                </div>
                {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                        <button onClick={handlePrevPage} disabled={currentPage === 0} className="p-2 rounded-full disabled:opacity-40 disabled:cursor-not-allowed text-navy-700 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors">
                            <FaChevronLeft />
                        </button>
                        <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                            {currentPage + 1} / {totalPages}
                        </span>
                        <button onClick={handleNextPage} disabled={currentPage === totalPages - 1} className="p-2 rounded-full disabled:opacity-40 disabled:cursor-not-allowed text-navy-700 dark:text-white hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors">
                            <FaChevronRight />
                        </button>
                    </div>
                )}
            </div>
            
            <div className="flex-grow overflow-hidden min-h-0">
                <div 
                    className="transition-transform duration-500 ease-in-out h-full" 
                    style={{ transform: `translateX(-${currentPage * 100}%)` }}
                >
                    <div className="flex h-full" style={{ width: `${totalPages > 0 ? totalPages * 100 : 100}%` }}>
                        {Array.from({ length: totalPages || 1 }).map((_, pageIndex) => (
                            <div key={pageIndex} className="w-full flex-shrink-0 p-1" style={{ width: `${100 / (totalPages || 1)}%` }}>
                                {isLoading ? (
                                    <p className="text-center text-gray-500 dark:text-gray-400 py-10">Loading events...</p>
                                ) : paginatedEvents.length > 0 && pageIndex === currentPage ? (
                                    <div className="space-y-4">
                                        {paginatedEvents.map(event => {
                                            const isAlreadyReserved = myReservationEventIds.includes(event.id);
                                            
                                            return user?.role === 'mentor' ? (
                                                <MentorEventDisplay
                                                    key={event.id}
                                                    event={event}
                                                    isExpanded={expandedEventId === event.id}
                                                    onToggle={() => handleToggleTeams(event.id)}
                                                />
                                            ) : (
                                                <UserEventDisplay
                                                    key={event.id}
                                                    event={event}
                                                    isReserving={reservingEventId === event.id}
                                                    isAlreadyReserved={isAlreadyReserved}
                                                    onReserveClick={() => handleReserveClick(event.id)}
                                                    onCancel={handleCancelReservation}
                                                    onSubmit={() => handleSubmitReservation(event)}
                                                    details={reservationDetails}
                                                    onDetailsChange={(e) => setReservationDetails(e.target.value)}
                                                />
                                            );
                                        })}
                                    </div>
                                ) : (
                                    pageIndex === 0 && (
                                         <div className="flex h-full flex-col items-center justify-center text-center py-10 bg-white dark:bg-navy-800 rounded-2xl shadow-inner">
                                            <FaRegClock className="text-5xl text-gray-300 dark:text-gray-500" />
                                            <p className="mt-4 text-lg font-semibold text-gray-500 dark:text-gray-400">No upcoming events.</p>
                                            <p className="text-sm text-gray-400 dark:text-gray-500">Check back later for new activities.</p>
                                        </div>
                                    )
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default UpcomingEventsCard;